(() => {
  // スクリプトスコープで状態を保持（DOMContentLoaded 内に置くと再実行時に
  // 二重初期化ガードが機能しないため、コールバック外で管理する）。
  const sections = [];
  const elementState = new WeakMap();
  let listenersBound = false;

  const isSafeUrl = (url) => typeof url === 'string' && /^(\/|https?:\/\/)/.test(url);

  const STAFF_LIST_URL = '/pages/staff-list';
  const normalizeShopName = (s) => String(s == null ? '' : s).trim().replace(/\s+/g, '').toLowerCase();

  // プロフィールのブランド名を「ブランド名 店舗名」にする（店舗名の要素はCSSで非表示のため統合して表示する）
  // LEEEP が要素を作り直さずテキストだけ書き換える再描画があるため、毎回テキストを見て必要なら直す。
  const mergeStaffBrandShop = (section) => {
    const brandEl = section.querySelector('.LeeepStaffBrand');
    const shopEl = section.querySelector('.LeeepStaffShop');
    if (!brandEl) return;
    const shopName = shopEl ? (shopEl.textContent || '').trim() : '';
    if (!shopName) return;
    // 結合後のテキストを再びブランド名として拾わないよう、退避した値を優先する
    // （ブランド名が空のスタッフでも拾い直さないよう、空文字の退避値もそのまま使う）
    const brandName = (brandEl.dataset.brandName !== undefined
      ? brandEl.dataset.brandName
      : (brandEl.textContent || '')).trim();
    const merged = brandName ? brandName + ' ' + shopName : shopName;
    brandEl.dataset.brandName = brandName;
    brandEl.dataset.shopName = shopName;
    if (brandEl.textContent.trim() !== merged) brandEl.textContent = merged;
  };

  // 複数店舗に所属している場合は、表示中の店舗名と一致するものを使う
  const pickShop = (shops, shopName) => {
    const list = Array.isArray(shops) ? shops : [];
    return list.find((s) => s && normalizeShopName(s.name) === normalizeShopName(shopName))
      || (list.length === 1 ? list[0] : null);
  };

  // 所属店舗（/v3/staffs）。描画イベントに店舗が含まれていないときのフォールバック。
  const fetchStaffShop = async (staffId, shopName) => {
    if (typeof LeeepCite === 'undefined' || !LeeepCite.site_id) return null;
    try {
      const res = await fetch(
        'https://cite.leeep.jp/v3/staffs?website_id=' + encodeURIComponent(LeeepCite.site_id)
        + '&staff_id=' + encodeURIComponent(staffId) + '&with_shop=1'
      );
      if (!res.ok) return null;
      const data = await res.json();
      const staff = data && data.staffs && data.staffs[0];
      return pickShop(staff && staff.staff && staff.staff.shops, shopName);
    } catch (e) {
      console.warn('[c_staff-ranking-detail] staff API failed:', e);
      return null;
    }
  };

  // パンくずに店舗名を追加：スタッフ一覧 > 店舗名 > スタッフ名
  // 先にリンクなしのテキストで挿入し、店舗IDが取れたらその店舗のスタッフ一覧へのリンクに差し替える。
  const insertShopCrumb = async (section, event) => {
    if (section.dataset.shopCrumbDone === 'true') return;
    const list = section.querySelector('.c_breadcrumb__list');
    const currentEl = section.querySelector('.c_breadcrumb__current');
    const currentLi = currentEl && currentEl.closest('.c_breadcrumb__item');
    const shopEl = section.querySelector('.LeeepStaffShop');
    const shopName = shopEl ? (shopEl.textContent || '').trim() : '';
    const staffId = new URLSearchParams(location.search).get('staff_id');
    if (!list || !currentLi || !shopName || !staffId) return;

    const li = document.createElement('li');
    li.className = 'c_breadcrumb__item';
    const span = document.createElement('span');
    span.className = 'c_breadcrumb__link';
    span.textContent = shopName;
    li.appendChild(span);
    list.insertBefore(li, currentLi);
    section.dataset.shopCrumbDone = 'true';

    const staffData = event && event.detail && event.detail.staff && event.detail.staff.staff;
    const shop = pickShop(staffData && staffData.shops, shopName)
      || await fetchStaffShop(staffId, shopName);
    if (!shop || !shop.id) return;

    const a = document.createElement('a');
    a.className = 'c_breadcrumb__link c_hover-opacity';
    a.href = STAFF_LIST_URL + '?shop_id=' + encodeURIComponent(String(shop.id));
    a.textContent = shopName;
    span.replaceWith(a);
  };

  // 絞り込み条件はURLではなく sessionStorage で渡す（c_custom-leeep.js が読み取り後に削除）。
  // URLに残すと LEEEP のフルページ送信で値が残り続け、クリア後の再送信で再適用される不具合になるため。
  const setPendingFilter = (key, value) => {
    try {
      // 宛先一覧を target で明示（3一覧でキー共有のためクロス消費を防ぐ）。
      sessionStorage.setItem('c_leeepPendingFilters', JSON.stringify({ target: 'staff-ranking', values: [key + ',' + value] }));
      return true;
    } catch (e) {
      return false; // private mode / 容量超過等
    }
  };

  // sessionStorage が使えない環境のフォールバック遷移先（LEEEP標準のスタッフ属性フィルタURL）。
  const buildNativeUrl = (rankingUrl, key, value) => {
    const sep = rankingUrl.indexOf('?') === -1 ? '?' : '&';
    return rankingUrl + sep + 'user_custom_props=' + encodeURIComponent(key + ',' + value);
  };

  // ローディング失敗時: leeep-tag-* 要素は保持し、エラーメッセージのみ追記
  const showLoadError = (section) => {
    const areas = section.querySelectorAll('[data-leeep-area]');
    areas.forEach((area) => {
      area.setAttribute('aria-busy', 'false');
      if (area.querySelector('.c_staff-ranking-detail__error')) return;
      const errorEl = document.createElement('p');
      errorEl.className = 'c_staff-ranking-detail__error';
      errorEl.textContent = 'コンテンツの読み込みに失敗しました。ページを再読み込みしてください。';
      area.appendChild(errorEl);
    });
  };

  const waitForLeeep = (ctx, count = 0) => {
    if (typeof LeeepCite !== 'undefined') {
      if (ctx.detailTagId) {
        LeeepCite.init({ tag_id: String(ctx.detailTagId) });
      }
      return;
    }
    if (count > 50) {
      showLoadError(ctx.section);
      return;
    }
    setTimeout(() => waitForLeeep(ctx, count + 1), 100);
  };

  // SNSリストの配置: PC=中カラム / SP=元位置
  const arrangeSns = (ctx) => {
    if (!ctx.snsLinkList || !ctx.separator || !ctx.staffInfoEl) return;
    const isPC = window.innerWidth > 999;
    if (isPC && !ctx.separator.contains(ctx.snsLinkList)) {
      ctx.separator.appendChild(ctx.snsLinkList);
    } else if (!isPC && !ctx.staffInfoEl.contains(ctx.snsLinkList)) {
      ctx.staffInfoEl.appendChild(ctx.snsLinkList);
    }
  };

  // rAF throttle: 連続mutationを1フレーム1回に集約
  const arrangeSnsThrottled = (ctx) => {
    if (ctx.rafPending) return;
    ctx.rafPending = true;
    requestAnimationFrame(() => {
      arrangeSns(ctx);
      ctx.rafPending = false;
    });
  };

  // MutationObserver でLEEEPの遅延再描画（フォロー押下等）に追従
  const setupArrangeSns = (ctx) => {
    if (ctx.arrangeSnsRegistered) return;
    if (!ctx.snsLinkList || !ctx.separator || !ctx.staffInfoEl) return;
    ctx.arrangeSnsRegistered = true;
    arrangeSns(ctx);
    const observer = new MutationObserver(() => arrangeSnsThrottled(ctx));
    observer.observe(ctx.section, { childList: true, subtree: true });
  };

  const processDetailRender = (ctx, event) => {
    const section = ctx.section;
    // 「このスタッフの投稿」は共通パーツ(c_staff-posts-tabs)で描画するため、
    // LEEEPネイティブの投稿タブ/一覧はCSSで非表示にし、ここでは何もしない。
    const followBtn = section.querySelector('.LeeepFollowButtonText');
    if (followBtn && followBtn.textContent.trim() === 'フォロー') {
      followBtn.textContent = 'フォローする';
    }
    const staffName = section.querySelector('.LeeepStaffDisplayName');
    const breadcrumbCurrent = section.querySelector('.c_breadcrumb__current');
    if (staffName && breadcrumbCurrent) {
      breadcrumbCurrent.textContent = staffName.textContent.trim();
    }
    insertShopCrumb(section, event);
    mergeStaffBrandShop(section);
    const heightEl = section.querySelector('.LeeepStaffHeight');
    const staffInfo = section.querySelector('.LeeepStaffInfo');
    if (heightEl && staffInfo && !elementState.has(heightEl)) {
      elementState.set(heightEl, { footAdded: true });
      staffInfo.appendChild(heightEl);

      const customProps = section.querySelector('.LeeepStaffCustomProperties');
      if (customProps) {
        const footProp = [...customProps.querySelectorAll('div')].find(
          (d) => d.textContent.trim().startsWith('足のサイズ')
        );
        if (footProp) {
          const footValue = footProp.textContent.split('：')[1]?.trim();
          if (footValue) {
            const footText = footValue.endsWith('cm') ? footValue : footValue + 'cm';
            heightEl.textContent = heightEl.textContent.trim() + '/' + footText;
          }
        }
      }
    }
    ctx.snsLinkList = section.querySelector('.LeeepStaffSnsLinkList');
    ctx.separator = section.querySelector('.LeeepStaffDetailSeparator');
    ctx.staffInfoEl = section.querySelector('.LeeepStaffInfo');

    if (ctx.snsLinkList && !ctx.snsLinkList.querySelector('.c_staff-ranking-detail__sns-icons')) {
      const snsLinks = ctx.snsLinkList.querySelectorAll('.LeeepStaffSnsLink');
      if (snsLinks.length > 0) {
        const wrapper = document.createElement('div');
        wrapper.className = 'c_staff-ranking-detail__sns-icons';
        snsLinks.forEach((link) => wrapper.appendChild(link));
        ctx.snsLinkList.prepend(wrapper);
      }
    }
    setupArrangeSns(ctx);

    const customPropsForTags = section.querySelector('.LeeepStaffCustomProperties');
    const secondary = section.querySelector('.LeeepStaffSecondary');
    if (customPropsForTags && secondary && !secondary.querySelector('.c_staff-ranking-detail__tags')) {
      const excludeKeys = ['足のサイズ', '靴下サイズ', '着用ファッション'];
      const tagsContainer = document.createElement('div');
      tagsContainer.className = 'c_staff-ranking-detail__tags';

      customPropsForTags.querySelectorAll('div').forEach((prop) => {
        const text = prop.textContent.trim();
        const parts = text.split('：');
        if (parts.length < 2) return;
        const key = parts[0].trim();
        const value = parts[1].trim();
        if (excludeKeys.includes(key) || !value) return;

        const tag = document.createElement('a');
        tag.className = 'c_staff-ranking-detail__tag c_hover-opacity';
        tag.href = ctx.rankingUrl;
        tag.textContent = '#' + key + '/' + value;
        tag.addEventListener('click', (e) => {
          // 中クリックや Cmd/Ctrl/Shift+クリック（新規タブ/ウィンドウ）は元タブが遷移しないため書き込まない。
          if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey) return;
          // sessionStorage が使えない場合は LEEEP標準のフィルタURLで遷移（機能・クリアは正常）。
          if (!setPendingFilter(key, value)) {
            e.preventDefault();
            location.href = buildNativeUrl(ctx.rankingUrl, key, value);
          }
        });
        tagsContainer.appendChild(tag);
      });

      if (tagsContainer.children.length > 0) {
        secondary.appendChild(tagsContainer);
      }
    }
    if (ctx.otherStaffTagId && !ctx.otherStaffInited) {
      ctx.otherStaffInited = true;
      const staffData = event.detail?.staff?.staff;
      const brandCode = staffData?.brands?.[0]?.code;
      if (brandCode) {
        LeeepCite.init({
          tag_id: String(ctx.otherStaffTagId),
          brands: [brandCode]
        });
      } else {
        LeeepCite.init({
          tag_id: String(ctx.otherStaffTagId)
        });
      }
    }
  };

  // スタッフカードを一覧（c_custom-leeep.js の reorderStaffInfo）と同じ並びに整形する。
  // 詳細ページは c_custom-leeep.js の対象外のため、ここで同等処理を行う。
  // 並び: 名前 → 店舗名 → 身長/足のサイズ（ブランド名は非表示）。
  const reorderOtherStaffCard = (link, staff) => {
    if (!link || link.dataset.__reordered) return;
    link.dataset.__reordered = '1';

    const nameEl = link.querySelector('.LeeepStaffListName');
    if (!nameEl) return;
    const heightEl = link.querySelector('.LeeepStaffListHeight');
    const shopEl = link.querySelector('.LeeepStaffListShopName');
    const brandEl = link.querySelector('.LeeepStaffListBrandName');

    if (brandEl) brandEl.style.display = 'none';

    // 身長/足のサイズのテキストを組み立てる
    const props = (staff && staff.staff && staff.staff.properties) || [];
    const footProp = props.find((p) => p && p.key && p.key.name === '足のサイズ');
    let heightText = '';
    if (heightEl) heightText = (heightEl.textContent || '').trim();
    else if (staff && staff.height) heightText = String(staff.height) + 'cm';

    let infoText = '';
    if (heightText) infoText += heightText;
    const footName = footProp && footProp.value && footProp.value.name;
    if (footName) {
      const footText = String(footName).endsWith('cm') ? String(footName) : footName + 'cm';
      if (heightText) infoText += '/';
      infoText += footText;
    }

    if (heightEl) heightEl.style.display = 'none';

    // 並び替え: 名前の直後に店舗名 → その後に身長/足
    if (shopEl) nameEl.after(shopEl);
    if (infoText && !link.querySelector('.LeeepStaffListHeightFoot')) {
      const infoEl = document.createElement('div');
      infoEl.className = 'LeeepStaffListHeightFoot';
      infoEl.textContent = infoText;
      (shopEl || nameEl).after(infoEl);
    }
  };

  const processOtherStaffRender = (ctx, event) => {
    const embeddedRoot = ctx.section.querySelector('#leeep-embedded-' + ctx.otherStaffTagId);
    if (embeddedRoot) {
      embeddedRoot.classList.add('is-leeep-staff-list');
    }

    const currentStaffId = new URLSearchParams(location.search).get('staff_id');
    if (currentStaffId) {
      const el = ctx.section.querySelector('[data-id="' + CSS.escape(currentStaffId) + '"]');
      if (el) {
        const parent = el.closest('a') || el.closest('.LeeepStaffListLink');
        if (parent) parent.remove();
      }
    }

    const staffs = (event && event.detail && event.detail.staffs) || [];
    const root = embeddedRoot || ctx.section;
    staffs.forEach((staff) => {
      if (!staff || !staff.id) return;
      const link = root.querySelector('.LeeepStaffListLink[data-id="' + CSS.escape(String(staff.id)) + '"]');
      reorderOtherStaffCard(link, staff);
    });
  };

  // 単一のリスナーで全セクションを処理（セクション単位で個別登録するとN個のリスナーが
  // 蓄積するため、トップレベルで一度だけ登録し sections 配列を回す）
  const handleRendered = (event) => {
    const tagId = String(event?.detail?.tag_id || '');
    if (!tagId) return;
    sections.forEach((ctx) => {
      const area = ctx.section.querySelector('[data-leeep-area]');
      if (area) area.setAttribute('aria-busy', 'false');
      if (tagId === String(ctx.detailTagId)) {
        processDetailRender(ctx, event);
      } else if (ctx.otherStaffTagId && tagId === String(ctx.otherStaffTagId)) {
        processOtherStaffRender(ctx, event);
      }
    });
  };

  const handleResize = () => {
    sections.forEach((ctx) => arrangeSnsThrottled(ctx));
  };

  const bindGlobalListeners = () => {
    if (listenersBound) return;
    listenersBound = true;
    document.addEventListener('onLeeepContentsRendered', handleRendered);
    window.addEventListener('resize', handleResize);
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.c_staff-ranking-detail__config').forEach((configEl) => {
      let config = {};
      try {
        config = JSON.parse(configEl.textContent);
      } catch (e) {
        return;
      }
      const section = document.getElementById('c_staff-ranking-detail-' + config.section_id);
      if (!section) return;
      // DOM 側のフラグで二重初期化を防ぐ（スクリプト再実行されてもDOMが同一なら有効）
      if (section.dataset.initialized === 'true') return;
      section.dataset.initialized = 'true';

      const ctx = {
        section,
        detailTagId: config.detail_tag_id,
        otherStaffTagId: config.other_staff_tag_id,
        rankingUrl: isSafeUrl(config.ranking_url) ? config.ranking_url : '/pages/staff-list',
        snsLinkList: null,
        separator: null,
        staffInfoEl: null,
        arrangeSnsRegistered: false,
        otherStaffInited: false,
        rafPending: false
      };
      sections.push(ctx);
      waitForLeeep(ctx);
    });

    if (sections.length > 0) bindGlobalListeners();
  });
})();
